// Disconnection reason definitions
export interface DisconnectionReasonInfo {
  reason: string;
  callStatus: 'ended' | 'not_connected' | 'error';
  description: string;
  /** How the badge reads, when the reason's own words would not say it plainly. */
  label?: string;
}

export const DISCONNECTION_REASONS: Record<string, DisconnectionReasonInfo> = {
  user_hangup: {
    reason: 'user_hangup',
    callStatus: 'ended',
    description: 'Expected behavior, user hangup the call.',
  },
  agent_hangup: {
    reason: 'agent_hangup',
    callStatus: 'ended',
    description: 'Expected behavior, AI agent hangup the call.',
  },
  call_transfer: {
    reason: 'call_transfer',
    callStatus: 'ended',
    description: 'Expected behavior, AI agent transferred the call.',
  },
  voicemail_reached: {
    reason: 'voicemail_reached',
    callStatus: 'ended',
    description: 'Expected behavior, if AI agent is configured with voicemail settings, and voicemail is reached.',
  },
  inactivity: {
    reason: 'inactivity',
    callStatus: 'ended',
    description: 'Expected behavior, call was terminated due to the "end_call_after_silence_ms" setting reached after long inactivity.',
  },
  max_duration_reached: {
    reason: 'max_duration_reached',
    callStatus: 'ended',
    description: 'Expected behavior, call was terminated due to maximum duration reached.',
  },
  dial_busy: {
    reason: 'dial_busy',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the number dialed is busy.',
  },
  dial_failed: {
    reason: 'dial_failed',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, dialing failed with no or unknown sip error code.',
  },
  dial_no_answer: {
    reason: 'dial_no_answer',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the number dialed did not answer.',
  },
  invalid_destination: {
    reason: 'invalid_destination',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the number dialed is invalid.',
  },
  telephony_provider_permission_denied: {
    reason: 'telephony_provider_permission_denied',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the sip trunk credentials are not authenticated.',
  },
  telephony_provider_unavailable: {
    reason: 'telephony_provider_unavailable',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the telephony provider is unavailable.',
  },
  sip_routing_error: {
    reason: 'sip_routing_error',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, the sip routing is going over too many hops or is in a loop.',
  },
  user_declined: {
    reason: 'user_declined',
    callStatus: 'not_connected',
    description: 'Outbound call not connected, user declined the call.',
  },
  error_user_not_joined: {
    reason: 'error_user_not_joined',
    callStatus: 'not_connected',
    label: 'Caller Never Joined',
    description: 'Web call not connected, the caller never joined, so the agent ended it after 60 seconds.',
  },
  concurrency_limit_reached: {
    reason: 'concurrency_limit_reached',
    callStatus: 'error',
    description: 'Error, concurrency limit reached, add a retry with exponential backoff. Or consider enterprise plan.',
  },
  no_valid_payment: {
    reason: 'no_valid_payment',
    callStatus: 'error',
    description: 'Error, no valid payment registered on file, or service shut down due to bill overdue.',
  },
  error_verbeo: {
    reason: 'error_verbeo',
    callStatus: 'error',
    description: 'Error, unspecified Verbeo side problem.',
  },
  error_unknown: {
    reason: 'error_unknown',
    callStatus: 'error',
    description: 'Error, unknown error.',
  },
  system_error: {
    reason: 'system_error',
    callStatus: 'error',
    description:
      'Error, the assistant could not recover from a technical fault mid-call. It apologized to the caller and ended the call rather than leaving them in silence.',
  },
};

export function getDisconnectionReasonInfo(reason: string | null): DisconnectionReasonInfo | null {
  if (!reason) return null;
  return DISCONNECTION_REASONS[reason] || null;
}

export function formatDisconnectionReason(reason: string | null): string {
  if (!reason) return 'N/A';
  return getDisconnectionReasonInfo(reason)?.label ?? reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

